import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import cn from "classnames";
import { DocLeadAnchor, InvestigationLead } from "../../../db/schema";
import { usePdfViewer } from "../../../hooks/usePdfViewer";
import PdfFloatingNav from "../../Forms/PdfFloatingNav";
import LeadFlagPopover, { LeadHover } from "../LeadFlagPopover";
import { FlagBox, HiddenFlagsChip, useHiddenFlags } from "./flagOverlay";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

export type PdfViewerHandle = { goToPage: (page: number) => void };

// A region to outline on the page, independent of leads: normalized 0-1
// boxes with the page on each. Used by the policy viewer for sections. The
// host drives the two tints (hover and active) and hears hover/click back,
// so a rail and the page stay in step.
export type PdfHighlight = { id: string; boxes: { page: number; x: number; y: number; w: number; h: number }[] };

function anchorOf(l: InvestigationLead): DocLeadAnchor | null {
  return (l.doc_anchor as DocLeadAnchor | null) || null;
}

/**
 * Read-only PDF viewer for investigation documents. Same hook + nav as the
 * Forms/Redactions editors, minus all field machinery. The rail drives page
 * jumps (key dates, flagged pages) through the imperative handle, mirroring
 * how CallDetail drives the transcript.
 *
 * Leads carrying doc_anchor boxes (normalized Textract line geometry, so
 * scanned and native PDFs behave the same) are painted as severity-tinted
 * highlights on the page; hover shows the flag, and the rail's cards drive
 * the deeper "active" tint through activeLeadId. Overlapping boxes can be
 * hidden one at a time (eye toggle) to reach the ones underneath; a hidden
 * box keeps a dashed outline that restores it.
 */
const PdfFileViewer = forwardRef<
  PdfViewerHandle,
  {
    url: string;
    initialPage?: number;
    onLoadError?: () => void;
    leads?: InvestigationLead[];
    activeLeadId?: string | null;
    onHoverLeads?: (ids: string[]) => void;
    highlights?: PdfHighlight[];
    activeHighlightId?: string | null;
    hoverHighlightId?: string | null;
    onHighlightHover?: (id: string | null) => void;
    onHighlightClick?: (id: string) => void;
  }
>(({ url, initialPage, onLoadError, leads = [], activeLeadId, onHoverLeads, highlights = [], activeHighlightId, hoverHighlightId, onHighlightHover, onHighlightClick }, ref) => {
    const pdf = usePdfViewer({ storageKey: "investigationPdfZoom" });
    const { scale } = pdf;

    // goToPage clamps against numPages, which is 0 until the document loads —
    // hold the request until then (covers ?page= deep links and early clicks).
    const pending = useRef<number | null>(initialPage ?? null);
    useEffect(() => {
      if (pdf.numPages > 0 && pending.current != null) {
        pdf.goToPage(pending.current);
        pending.current = null;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pdf.numPages]);

    useImperativeHandle(ref, () => ({
      goToPage: (page: number) => {
        if (pdf.numPages > 0) pdf.goToPage(page);
        else pending.current = page;
      },
    }));

    const [hoverFlag, setHoverFlag] = useState<LeadHover | null>(null);
    const { hidden, hide, restore, reset } = useHiddenFlags();
    const pageLeads = leads.filter((l) => l.status !== "dismissed" && anchorOf(l)?.page === pdf.currentPage);
    const showFlags = (flagged: InvestigationLead[], el: HTMLElement) => {
      const r = el.getBoundingClientRect();
      const below = r.bottom + 220 < window.innerHeight;
      setHoverFlag({ leads: flagged, x: Math.min(r.left, window.innerWidth - 400), y: below ? r.bottom + 6 : r.top - 6, below });
      onHoverLeads?.(flagged.map((f) => f.id));
    };
    const clearFlags = () => {
      setHoverFlag(null);
      onHoverLeads?.([]);
    };

    return (
      <div className="relative h-full">
        <div
          ref={pdf.containerRef}
          tabIndex={-1}
          className={cn("h-full overflow-auto bg-base-300 p-8 outline-none", pdf.panCursorClass)}
          onMouseDown={pdf.handleContainerMouseDown}
          onMouseMove={pdf.handleContainerMouseMove}
          onMouseUp={pdf.handleContainerMouseEnd}
          onMouseLeave={pdf.handleContainerMouseEnd}
        >
          <div
            ref={pdf.canvasRef}
            className={cn("relative mx-auto bg-white shadow-xl select-none", pdf.panCursorClass)} /* paper — stays white in dark mode */
            style={{
              width: pdf.pdfDimensions.width * scale || 612,
              height: pdf.pdfDimensions.height * scale || 792,
              transition: pdf.pageReady ? "none" : "width 0.15s ease, height 0.15s ease",
            }}
          >
            <Document
              file={url}
              onLoadSuccess={pdf.onDocumentLoadSuccess}
              onLoadError={onLoadError}
              loading={null}
              error={<div className="flex items-center justify-center h-96 text-error">Failed to load PDF</div>}
            >
              {pdf.numPages > 0 && (
                <div className="transition-opacity duration-200" style={{ opacity: pdf.pageReady ? 1 : 0 }}>
                  <Page
                    key={`page-${pdf.currentPage}-${scale}`}
                    pageNumber={pdf.currentPage}
                    scale={scale}
                    onLoadSuccess={pdf.onPageLoadSuccess}
                    onRenderSuccess={() => pdf.setPageReady(true)}
                    loading={<span />}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                </div>
              )}
            </Document>
            {/* Section highlights (policy viewer): invisible until hovered from
                either side, a light tint on hover, a deeper one when active. */}
            {pdf.pageReady &&
              highlights.map((h) =>
                h.boxes
                  .filter((b) => b.page === pdf.currentPage)
                  .map((b, bi) => {
                    const active = h.id === activeHighlightId;
                    const hover = h.id === hoverHighlightId;
                    return (
                      <div
                        key={`${h.id}-${bi}`}
                        className={cn(
                          "absolute rounded-sm border transition-colors duration-150 cursor-pointer",
                          active ? "bg-blue-500/25 border-blue-600 z-[2]" : hover ? "bg-blue-500/10 border-blue-400 z-[1]" : "border-transparent"
                        )}
                        style={{
                          left: `${(b.x - 0.006) * 100}%`,
                          top: `${(b.y - 0.004) * 100}%`,
                          width: `${(b.w + 0.012) * 100}%`,
                          height: `${(b.h + 0.008) * 100}%`,
                        }}
                        onMouseEnter={() => onHighlightHover?.(h.id)}
                        onMouseLeave={() => onHighlightHover?.(null)}
                        onClick={() => onHighlightClick?.(h.id)}
                      />
                    );
                  })
              )}
            {/* Lead highlights: normalized boxes scale with the page for free. */}
            {pdf.pageReady &&
              pageLeads.map((l) =>
                (anchorOf(l)?.boxes || []).map((b, bi) => (
                  <FlagBox
                    key={`${l.id}-${bi}`}
                    blend
                    severity={l.severity}
                    active={l.id === activeLeadId}
                    hidden={hidden.has(l.id)}
                    control={bi === 0}
                    onHide={() => hide(l.id)}
                    onRestore={() => restore(l.id)}
                    onMouseEnter={(el) => showFlags([l], el)}
                    onMouseLeave={clearFlags}
                    style={{
                      left: `${(b.x - 0.004) * 100}%`,
                      top: `${(b.y - 0.004) * 100}%`,
                      width: `${(b.w + 0.008) * 100}%`,
                      height: `${(b.h + 0.008) * 100}%`,
                    }}
                  />
                ))
              )}
            {!pdf.pageReady && (
              <div className="absolute inset-0 flex items-center justify-center z-10">
                <span className="loading loading-spinner loading-md text-base-content/30" />
              </div>
            )}
          </div>
        </div>
        {pdf.numPages > 0 && (
          <PdfFloatingNav
            currentPage={pdf.currentPage}
            numPages={pdf.numPages}
            scale={scale}
            onPrevPage={pdf.prevPage}
            onNextPage={pdf.nextPage}
            onZoomIn={pdf.zoomIn}
            onZoomOut={pdf.zoomOut}
            onZoomFit={pdf.zoomFit}
            minScale={0.25}
            maxScale={3}
          />
        )}
        <HiddenFlagsChip count={hidden.size} onReset={reset} />
        {hoverFlag && <LeadFlagPopover hover={hoverFlag} corner={(l) => (l.page ? `p. ${l.page}` : null)} />}
      </div>
    );
  }
);

export default PdfFileViewer;
